import React, { useState, useEffect, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import NumberFormat from 'react-number-format';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputLabel from '@material-ui/core/InputLabel';
import Tooltip from '@material-ui/core/Tooltip';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';

import ConfirmEmailDialog from 'app/components/dialogs/ConfirmEmailDialog';
import ReconcileAccountsDialog from 'app/components/dialogs/ReconcileAccountsDialog';

import SaveButton from 'app/components/buttons/SaveButton';
import Loading from 'app/components/loaders/Loading';

import States from 'app/utils/States';
import DefaultFieldValue from 'app/utils/DefaultFieldValue';
import IndustryTextField from 'app/components/textfields/IndustryTextField';
import Field from 'app/components/form/Field';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import { paper } from 'app/styles/core';

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    marginRight: {
        marginRight: theme.spacing(3),
    },
    marginTop: {
        marginTop: theme.spacing(3),
    },
    iconError: {
        color: theme.palette.error.main
    },
    iconSuccess: {
        color: theme.palette.success.main
    }
}));

export default function Profile(props) {

    const { admin, urlPrefix, getUser, deleted } = props;
    const classes = useStyles();
    const api = useApi();
    const { app } = useApp();

    const [ user, setUser ] = useState({
        prefix: "Mr.",
        first: "",
        middle: "",
        last: "",
        suffix: "",
        nickname: "",
        salutation: "",
        birth_date: "",

        email: "",
        email_verified_at: null,
        phone_mobile: "",

        home_email: "",
        phone_home: "",
        home_mobile: "",
        home_fax: "",

        address_one: "",
        address_two: "",
        city: "",
        state: "",
        zip: "",

        industry: "8699",
        employer: "",
        position: "",

        work_email: "",
        phone_work: "",
        work_fax: "",

        work_address_one: "",
        work_address_two: "",
        work_city: "",
        work_state: "",
        work_zip: "",

        marital_status: "",
        spouse: "",
        spouse_nickname: "",
        spouse_email: "",
        spouse_mobile: "",
        spouse_company: "",

        river_position: "",
        board_member: "",
        king_gift_group: "",
        kings_council: "",
        tccf_past_president: "",
        year_joined: "",
        military_branch: "",
        military_rank: "",
        military_years_of_service: "",

        full_name: "",
    });

    const [ customValues, setCustomValues ] = useState({});
    const [ customFields, setCustomFields ] = useState({});

    const [ errors, setErrors ] = useState([]);

    const [ personalLoading, setPersonalLoading ] = useState(false);
    const [ notificationLoading, setNotificationLoading ] = useState(false);
    const [ homeContactLoading, setHomeContactLoading ] = useState(false);
    const [ homeAddressLoading, setHomeAddressLoading ] = useState(false);
    const [ workLoading, setWorkLoading ] = useState(false);
    const [ workContactLoading, setWorkContactLoading ] = useState(false);
    const [ workAddressLoading, setWorkAddressLoading ] = useState(false);
    const [ spouseLoading, setSpouseLoading ] = useState(false);
    const [ cavalierLoading, setCavalierLoading ] = useState(false);
    const [ otherLoading, setOtherLoading ] = useState(false);
    const [ verifyEmailLoading, setVerifyEmailLoading ] = useState(false);

    const [ confirmEmailDialogOpen, setConfirmEmailDialogOpen ] = useState(false);
    const [ reconcileAccountsDialogOpen, setReconcileAccountsDialogOpen ] = useState(false);

    // Getter functions ----------------------------------------

    useEffect(() => {
        handleAllLoading(true);

        if (props.user !== null) {
            setUser(props.user);
            handleAllLoading(false);
        }
    }, [props.user]);

    const mapCustomValues = (fields, values) => {

        const newValues = {};

        // Map the values to their fields
        fields.forEach(f => {
            const v = values ? values.find(val => (val.field_id === f.id)) : false;

            if (v) {
                newValues[f.id] = v.value;
            } else {
                const dv = DefaultFieldValue(f);
                if (dv) newValues[f.id] = dv;
            }
        });

        setCustomValues(newValues);
    }

    useEffect(() => {
        if (app.club?.fields?.length > 0) mapCustomValues(app.club.fields, props.customValues);
    }, [props.customValues]);

    useEffect(() => {
        if (admin) setCustomFields(app.club.fields);
        else setCustomFields(app.club.fields.filter(f => f.options?.admin_view === false));
    }, [])

    // Setter functions ------------------------------------------

    const handleChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setUser({ ...user, [name]: value });
    };

    const handleIndustry = (e, dataSet) => {
        const value = (dataSet !== null) ? dataSet.value : "";

        if (isError('industry')) delete errors.industry;

        setUser({ ...user, industry: value });
    }

    const handleCustomValue = (index , event) => {

        let value;

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {
            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        };

        if (errors?.custom_values?.[index]) delete errors.custom_values[index];

        setCustomValues({ ...customValues, [index]: { value: value} });

    };

    // Posting functions ----------------------------------------

    const postPersonal = () => {
        setPersonalLoading(true);

        const data = {
            prefix: user.prefix,
            first: user.first,
            middle: user.middle,
            last: user.last,
            suffix: user.suffix,
            nickname: user.nickname,
            salutation: user.salutation,
            birth_date: user.birth_date
        }

        api.post(urlPrefix + '/personal', data)
            .then(() => getUser())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setPersonalLoading(false));
    }

    const postNotification = () => {
        setNotificationLoading(true);

        const data = {
            email: user.email,
            phone_mobile: user.phone_mobile,
        }

        api.post(urlPrefix + '/notification', data)
            .then(() => getUser())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setNotificationLoading(false));
    }

    const postHomeContact = () => {
        setHomeContactLoading(true);

        const data = {
            home_email: user.home_email,
            phone_home: user.phone_home,
            home_mobile: user.home_mobile,
            home_fax: user.home_fax
        }

        api.post(urlPrefix + '/contact/home', data)
            .then(() => getUser())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setHomeContactLoading(false));
    }

    const postHomeAddress = () => {
        setHomeAddressLoading(true);

        const data = {
            address_one: user.address_one,
            address_two: user.address_two,
            city: user.city,
            state: user.state,
            zip: user.zip,
        }

        api.post(urlPrefix + '/address/home', data)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setHomeAddressLoading(false));
    }

    const postWork = () => {
        setWorkLoading(true);

        const data = {
            industry: user.industry,
            employer: user.employer,
            position: user.position,
        }

        api.post(urlPrefix + '/work', data)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setWorkLoading(false));
    }

    const postWorkContact = () => {
        setWorkContactLoading(true);

        const data = {
            work_email: user.work_email,
            phone_work: user.phone_work,
            work_fax: user.work_fax,
        }

        api.post(urlPrefix + '/contact/work', data)
            .then(() => getUser())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setWorkContactLoading(false));
    }

    const postWorkAddress = () => {
        setWorkAddressLoading(true);

        const data = {
            work_address_one: user.work_address_one,
            work_address_two: user.work_address_two,
            work_city: user.work_city,
            work_state: user.work_state,
            work_zip: user.work_zip,
        }

        api.post(urlPrefix + '/address/work', data)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setWorkAddressLoading(false));
    }

    const postSpouse = () => {
        setSpouseLoading(true);

        const data = {
            marital_status: user.marital_status,
            spouse: user.spouse,
            spouse_nickname: user.spouse_nickname,
            spouse_email: user.spouse_email,
            spouse_mobile: user.spouse_mobile,
            spouse_company: user.spouse_company,
        }

        api.post(urlPrefix + '/spouse', data)
            .then(() => getUser())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setSpouseLoading(false));
    }

    const postCavalier = () => {
        setCavalierLoading(true);

        const data = {
            river_position: user.river_position,
            board_member: user.board_member,
            king_gift_group: user.king_gift_group,
            kings_council: user.kings_council,
            tccf_past_president: user.tccf_past_president,
            year_joined: user.year_joined,
            military_branch: user.military_branch,
            military_rank: user.military_rank,
            military_years_of_service: user.military_years_of_service
        }

        api.post(urlPrefix + '/cavalier', data)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setCavalierLoading(false));
    }

    const postOther = () => {
        setOtherLoading(true);

        api.post(urlPrefix + '/other', {custom_values: customValues})
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setOtherLoading(false));
    }

    const isError = field => {
        return (errors[field]);
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const getEmailHelperText = () => {
        if (isError('email')) {
            return getErrorMessage('email');
        } else if (user.email_verified_at === null) {
            return (
                <Fragment>
                    Please verify your email address.
                    <Button
                        variant="text"
                        color="primary"
                        onClick={verifyEmail}
                        disabled={verifyEmailLoading || notificationLoading || deleted}
                        >
                        Verify
                    </Button>
                </Fragment>
            )
        } else {
            return 'Email address has been verified.';
        }
    }

    const getIndustryMessage = () => {
        if (isError('industry')) return errors.industry;
        return "Select from the list of available industries.";
    }

    const getBirthDateMessage = () => {
        if (isError('birth_date')) return errors.birth_date;
        return "Administrative Only. Will not be published in Directory.";
    }

    // Helper Functions ----------------------------------------

    const handleAllLoading = val => {
        setPersonalLoading(val);
        setHomeContactLoading(val);
        setHomeAddressLoading(val);
        setWorkLoading(val);
        setWorkContactLoading(val);
        setWorkAddressLoading(val);
        setSpouseLoading(val);
        setCavalierLoading(val);
        setOtherLoading(val);
    }

    // Dialog Helpers ----------------------------------------

    const verifyEmail = () => {
        setVerifyEmailLoading(true);

        api.get(urlPrefix + '/verify/email')
            .then(() => setConfirmEmailDialogOpen(true))
            .finally(() => setVerifyEmailLoading(false));
    }

    const closeConfirmEmailDialog = () => {
        getUser();
        setConfirmEmailDialogOpen(false);
    }

    const reconcileAccounts = () => setReconcileAccountsDialogOpen(true);

    const closeReconcileAccountsDialog = () => {
        getUser();
        setReconcileAccountsDialogOpen(false);
    }

    return (
        <form noValidate autoComplete="off">
            {(!admin) &&
                <Paper className={classes.paper}>
                    <Grid container
                        alignItems="center"
                        justify="space-between"
                        spacing={3}
                        >
                        <Grid item>
                            <Typography variant="h6" component="h3">
                                Profile
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Tooltip title={admin ? "Users can use their username or their email address to login." : "You can use your username or your email address to login."}>
                                <Typography variant="subtitle2" component="h6">
                                    Username: <Typography variant="subtitle2" color="primary" component="span">{user.username}</Typography>
                                </Typography>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Typography color="textSecondary">
                        Welcome to your personal profile. Here you can modify your personal information, contact information, addresses and more. You can also access your clubs, payment information, security and settings by using the profile sidebar.
                    </Typography>
                    {(!notificationLoading && user.email_verified_at === null) &&
                        <Grid
                            container
                            alignItems="center"
                            justify="space-between"
                            spacing={3}
                            className={classes.marginTop}
                            >
                            <Grid item>
                                <Typography color="primary" variant="subtitle2" display="inline" className={classes.marginRight}>
                                    Verify your email address to allow for two-step authentication and account reconciliation.
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={verifyEmail}
                                    disabled={verifyEmailLoading}
                                    >
                                    Verify Email
                                </Button>
                            </Grid>
                        </Grid>
                    }
                    {(!notificationLoading && user.email_verified_at !== null && user.associated_accounts > 0 && !app.club.meta.club_isolation) &&
                        <Grid
                            container
                            alignItems="center"
                            justify="space-between"
                            spacing={3}
                            classes={{ root: classes.marginTop }}
                            >
                            <Grid item>
                                <Typography color="primary" variant="subtitle2" display="inline" className={classes.marginRight}>
                                    {user.associated_accounts} other account{(user.associated_accounts > 1) ? 's are' : ' is'} associated with this email address.
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={reconcileAccounts}
                                    >
                                    Reconcile Accounts
                                </Button>
                            </Grid>
                        </Grid>
                    }
                </Paper>
            }
            <Paper className={classes.paper}>
                <Loading isLoading={personalLoading} />
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Personal Information
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4} md={12} lg={2}>
                        <TextField
                            id="input-prefix"
                            label="Prefix"
                            value={user.prefix}
                            onChange={handleChange('prefix')}
                            fullWidth
                            required
                            error={isError('prefix')}
                            helperText={getErrorMessage('prefix')}
                            variant='outlined'
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={8} md={12} lg={5}>
                        <TextField
                            id="input-first"
                            label="First"
                            value={user.first}
                            onChange={handleChange('first')}
                            fullWidth
                            required
                            error={isError('first')}
                            helperText={getErrorMessage('first')}
                            variant='outlined'
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={5}>
                        <TextField
                            id="input-middle"
                            label="Middle"
                            value={user.middle}
                            onChange={handleChange('middle')}
                            fullWidth
                            error={isError('middle')}
                            helperText={getErrorMessage('middle')}
                            variant='outlined'
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={5}>
                        <TextField
                            id="input-last"
                            label="Last"
                            value={user.last}
                            onChange={handleChange('last')}
                            fullWidth
                            required
                            error={isError('last')}
                            helperText={getErrorMessage('last')}
                            variant='outlined'
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={4} md={12} lg={2}>
                        <TextField
                            id="input-suffix"
                            label="Suffix"
                            value={user.suffix}
                            onChange={handleChange('suffix')}
                            fullWidth
                            error={isError('suffix')}
                            helperText={getErrorMessage('suffix')}
                            variant='outlined'
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={8} md={12} lg={5}>
                        <TextField
                            id="input-nickname"
                            label="Nickname"
                            value={user.nickname}
                            onChange={handleChange('nickname')}
                            fullWidth
                            error={isError('nickname')}
                            helperText={getErrorMessage('nickname')}
                            variant='outlined'
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={8} md={12} lg={5}>
                        <TextField
                            id="input-salutation"
                            label="Salutation"
                            value={user.salutation}
                            onChange={handleChange('salutation')}
                            fullWidth
                            error={isError('salutation')}
                            helperText={getErrorMessage('salutation')}
                            variant='outlined'
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={8} md={12} lg={5}>
                        <NumberFormat
                            format="##/##/####"
                            customInput={TextField}
                            label="Birth Date"
                            value={user.birth_date}
                            onChange={handleChange('birth_date')}
                            fullWidth
                            variant='outlined'
                            error={isError('birth_date')}
                            helperText={getBirthDateMessage()}
                            disabled={deleted}
                            placeholder="mm/dd/yyyy"
                        />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.marginTop}>
                    <SaveButton
                        disabled={personalLoading || deleted}
                        variant="contained"
                        color="primary"
                        onClick={postPersonal}
                        >
                        Update
                    </SaveButton>
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={notificationLoading} />
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Contact Information
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="subtitle2" component="h6" gutterBottom color="primary">
                            For Administrative Use Only. Will not be published in the Directory.
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <FormControl variant="outlined" error={isError('email')} fullWidth>
                            <InputLabel htmlFor="input-email">Email Address</InputLabel>
                            <OutlinedInput
                                id="input-email"
                                value={user.email}
                                onChange={handleChange('email')}
                                endAdornment={
                                    <InputAdornment position="end">
                                        {(user.email_verified_at === null) ? (
                                            <ErrorIcon className={classes.iconError}/>
                                        ) : (
                                            <CheckCircleIcon className={classes.iconSuccess}/>
                                        )}
                                    </InputAdornment>
                                }
                                aria-describedby="email-helper-text"
                                classes={{ input: classes.urlInput }}
                                required
                                labelWidth={110}
                            />
                            <FormHelperText id="email-helper-text">{getEmailHelperText()}</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <NumberFormat
                            format="(###) ###-####"
                            customInput={TextField}
                            id="input-mobile-phone"
                            type="tel"
                            label="Mobile Phone"
                            value={user.phone_mobile}
                            onChange={handleChange('phone_mobile')}
                            fullWidth
                            variant='outlined'
                            error={isError('phone_mobile')}
                            helperText={getErrorMessage('phone_mobile')}
                            disabled={deleted}
                        />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.marginTop}>
                    <SaveButton
                        disabled={notificationLoading || deleted}
                        variant="contained"
                        color="primary"
                        onClick={postNotification}
                        >
                        Update
                    </SaveButton>
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={homeContactLoading} />
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Home Contact Information
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <TextField
                            id="home-email"
                            label="Email Address"
                            value={user.home_email}
                            onChange={handleChange('home_email')}
                            fullWidth
                            variant='outlined'
                            error={isError('home_email')}
                            helperText={getErrorMessage('home_email')}
                            disabled={deleted}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <NumberFormat
                            format="(###) ###-####"
                            customInput={TextField}
                            id="input-home-phone"
                            type="tel"
                            label="Home Phone"
                            value={user.phone_home}
                            onChange={handleChange('phone_home')}
                            fullWidth
                            variant='outlined'
                            error={isError('phone_home')}
                            helperText={getErrorMessage('phone_home')}
                            disabled={deleted}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <NumberFormat
                            format="(###) ###-####"
                            customInput={TextField}
                            type="tel"
                            label="Mobile Phone"
                            value={user.home_mobile}
                            onChange={handleChange('home_mobile')}
                            fullWidth
                            variant='outlined'
                            error={isError('home_mobile')}
                            helperText={getErrorMessage('home_mobile')}
                            disabled={deleted}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <NumberFormat
                            format="(###) ###-####"
                            customInput={TextField}
                            type="tel"
                            label="Home Fax"
                            value={user.home_fax}
                            onChange={handleChange('home_fax')}
                            fullWidth
                            variant='outlined'
                            error={isError('home_fax')}
                            helperText={getErrorMessage('home_fax')}
                            disabled={deleted}
                        />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.marginTop}>
                    <SaveButton
                        disabled={homeContactLoading}
                        variant="contained"
                        color="primary"
                        onClick={postHomeContact}
                        >
                        Update
                    </SaveButton>
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={homeAddressLoading} />
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Home Address
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <TextField
                            id="input-address-one"
                            label="Address One"
                            value={user.address_one}
                            onChange={handleChange('address_one')}
                            fullWidth
                            variant='outlined'
                            error={isError('address_one')}
                            helperText={getErrorMessage('address_one')}
                            disabled={deleted}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <TextField
                            id="input-address-two"
                            label="Address Two"
                            value={user.address_two}
                            onChange={handleChange('address_two')}
                            fullWidth
                            variant='outlined'
                            error={isError('address_two')}
                            helperText={getErrorMessage('address_two')}
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={5} md={12} lg={6}>
                        <TextField
                            id="input-city"
                            label="City"
                            value={user.city}
                            onChange={handleChange('city')}
                            fullWidth
                            variant='outlined'
                            error={isError('city')}
                            helperText={getErrorMessage('city')}
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={4} md={12} lg={2}>
                        <TextField
                            id="input-state"
                            select
                            label="State"
                            value={user.state}
                            onChange={handleChange('state')}
                            fullWidth
                            variant='outlined'
                            error={isError('state')}
                            helperText={getErrorMessage('state')}
                            disabled={deleted}
                            >
                            <MenuItem
                                key="none"
                                value=""
                                >
                                Select a State...
                            </MenuItem>
                            {States.map( state => {
                                return (
                                    <MenuItem
                                        key={state.abbreviation}
                                        value={state.abbreviation}
                                        >
                                        {state.name}
                                    </MenuItem>
                                )
                            })}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3} md={12} lg={4}>
                        <TextField
                            id="input-zip"
                            label="ZIP"
                            value={user.zip}
                            onChange={handleChange('zip')}
                            fullWidth
                            variant='outlined'
                            error={isError('zip')}
                            helperText={getErrorMessage('zip')}
                            disabled={deleted}
                            />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.marginTop}>
                    <SaveButton
                        disabled={homeAddressLoading || deleted}
                        variant="contained"
                        color="primary"
                        onClick={postHomeAddress}
                        >
                        Update
                    </SaveButton>
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={workLoading} />
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Work Information
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4} md={12} lg={4}>
                        <IndustryTextField
                            value={user.industry}
                            onChange={handleIndustry}
                            groupBy={'individual_cat'}
                            error={isError('industry')}
                            helperText={getIndustryMessage()}
                            disabled={deleted}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={12} lg={4}>
                        <TextField
                            id="input-employer"
                            label="Employer"
                            value={user.employer}
                            onChange={handleChange('employer')}
                            fullWidth
                            variant='outlined'
                            error={isError('employer')}
                            helperText={getErrorMessage('employer')}
                            disabled={deleted}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={12} lg={4}>
                        <TextField
                            id="input-position"
                            label="Position"
                            value={user.position}
                            onChange={handleChange('position')}
                            fullWidth
                            variant='outlined'
                            error={isError('position')}
                            helperText={getErrorMessage('position')}
                            disabled={deleted}
                        />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.marginTop}>
                    <SaveButton
                        disabled={workLoading || deleted}
                        variant="contained"
                        color="primary"
                        onClick={postWork}
                        >
                        Update
                    </SaveButton>
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={workContactLoading} />
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Work Contact Information
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <TextField
                            label="Work Email"
                            value={user.work_email}
                            onChange={handleChange('work_email')}
                            fullWidth
                            error={isError('work_email')}
                            helperText={getErrorMessage('work_email')}
                            variant='outlined'
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <NumberFormat
                            format="(###) ###-####"
                            customInput={TextField}
                            type="tel"
                            label="Work Phone"
                            value={user.phone_work}
                            onChange={handleChange('phone_work')}
                            fullWidth
                            variant='outlined'
                            error={isError('phone_work')}
                            helperText={getErrorMessage('phone_work')}
                            disabled={deleted}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <NumberFormat
                            format="(###) ###-####"
                            customInput={TextField}
                            type="tel"
                            label="Work Fax"
                            value={user.work_fax}
                            onChange={handleChange('work_fax')}
                            fullWidth
                            variant='outlined'
                            error={isError('work_fax')}
                            helperText={getErrorMessage('work_fax')}
                            disabled={deleted}
                        />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.marginTop}>
                    <SaveButton
                        disabled={workContactLoading || deleted}
                        variant="contained"
                        color="primary"
                        onClick={postWorkContact}
                        >
                        Update
                    </SaveButton>
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={workAddressLoading} />
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Work Address
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <TextField
                            id="input-work-address-one"
                            label="Work Address One"
                            value={user.work_address_one}
                            onChange={handleChange('work_address_one')}
                            fullWidth
                            variant='outlined'
                            error={isError('work_address_one')}
                            helperText={getErrorMessage('work_address_one')}
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <TextField
                            id="input-work-address-two"
                            label="Work Address Two"
                            value={user.work_address_two}
                            onChange={handleChange('work_address_two')}
                            fullWidth
                            variant='outlined'
                            error={isError('work_address_two')}
                            helperText={getErrorMessage('work_address_two')}
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={5} md={12} lg={6}>
                        <TextField
                            id="input-work-city"
                            label="Work City"
                            value={user.work_city}
                            onChange={handleChange('work_city')}
                            fullWidth
                            variant='outlined'
                            error={isError('work_city')}
                            helperText={getErrorMessage('work_city')}
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={4} md={12} lg={2}>
                        <TextField
                            id="input-work-state"
                            select
                            label="Work State"
                            value={user.work_state}
                            onChange={handleChange('work_state')}
                            fullWidth
                            variant='outlined'
                            error={isError('work_state')}
                            helperText={getErrorMessage('work_state')}
                            disabled={deleted}
                            >
                            <MenuItem
                                key="none"
                                value=""
                                >
                                Select a State...
                            </MenuItem>
                            {States.map( state => {
                                return (
                                    <MenuItem key={state.abbreviation} value={state.abbreviation} >{state.name}</MenuItem>
                                )
                            })}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3} md={12} lg={4}>
                        <TextField
                            id="input-work-zip"
                            label="Work ZIP"
                            value={user.work_zip}
                            onChange={handleChange('work_zip')}
                            fullWidth
                            variant='outlined'
                            error={isError('work_zip')}
                            helperText={getErrorMessage('work_zip')}
                            disabled={deleted}
                            />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.marginTop}>
                    <SaveButton
                        disabled={workAddressLoading || deleted}
                        variant="contained"
                        color="primary"
                        onClick={postWorkAddress}
                        >
                        Update
                    </SaveButton>
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={spouseLoading} />
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Spouse Information
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4} md={12} lg={4}>
                        <TextField
                            id="input-marital-status"
                            select
                            label="Marital Status"
                            value={user.marital_status}
                            onChange={handleChange('marital_status')}
                            fullWidth
                            error={isError('marital_status')}
                            helperText={getErrorMessage('marital_status')}
                            variant='outlined'
                            disabled={deleted}
                            >
                            <MenuItem value="" >n/a</MenuItem>
                            <MenuItem value="Married" >Married</MenuItem>
                            <MenuItem value="Single" >Single</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={8} md={12} lg={8}>
                        <TextField
                            id="input-spouse"
                            label="Spouse"
                            value={user.spouse}
                            onChange={handleChange('spouse')}
                            fullWidth
                            error={isError('spouse')}
                            helperText={getErrorMessage('spouse')}
                            variant='outlined'
                            disabled={deleted}
                            />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <TextField
                            label="Spouse Nickname"
                            value={user.spouse_nickname}
                            onChange={handleChange('spouse_nickname')}
                            fullWidth
                            error={isError('spouse_nickname')}
                            helperText={getErrorMessage('spouse_nickname')}
                            variant='outlined'
                            />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <TextField
                            label="Spouse Email"
                            value={user.spouse_email}
                            onChange={handleChange('spouse_email')}
                            fullWidth
                            error={isError('spouse_email')}
                            helperText={getErrorMessage('spouse_email')}
                            variant='outlined'
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <NumberFormat
                            format="(###) ###-####"
                            customInput={TextField}
                            type="tel"
                            label="Spouse Mobile"
                            value={user.spouse_mobile}
                            onChange={handleChange('spouse_mobile')}
                            fullWidth
                            variant='outlined'
                            error={isError('spouse_mobile')}
                            helperText={getErrorMessage('spouse_mobile')}
                            disabled={deleted}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <TextField
                            label="Spouse Company"
                            value={user.spouse_company}
                            onChange={handleChange('spouse_company')}
                            fullWidth
                            error={isError('spouse_company')}
                            helperText={getErrorMessage('spouse_company')}
                            variant='outlined'
                            />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.marginTop}>
                    <SaveButton
                        disabled={spouseLoading || deleted}
                        variant="contained"
                        color="primary"
                        onClick={postSpouse}
                        >
                        Update
                    </SaveButton>
                </Typography>
            </Paper>
            {((app.club.id === 5 || app.user.id === 1) && admin) &&
                <Paper className={classes.paper}>
                    <Loading isLoading={cavalierLoading} />
                    <Grid container
                        alignItems="center"
                        justify="space-between"
                        spacing={3}
                        >
                        <Grid item>
                            <Typography variant="h6" component="h3" gutterBottom>
                                Cavalier Information
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={12} lg={6}>
                            <TextField
                                label="Board Member"
                                value={user.board_member}
                                onChange={handleChange('board_member')}
                                fullWidth
                                error={isError('board_member')}
                                helperText={getErrorMessage('board_member')}
                                variant='outlined'
                                />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={6}>
                            <TextField
                                label="River Position"
                                value={user.river_position}
                                onChange={handleChange('river_position')}
                                fullWidth
                                error={isError('river_position')}
                                helperText={getErrorMessage('river_position')}
                                variant='outlined'
                                />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={6}>
                            <TextField
                                label="King Gift Group"
                                value={user.king_gift_group}
                                onChange={handleChange('king_gift_group')}
                                fullWidth
                                error={isError('king_gift_group')}
                                helperText={getErrorMessage('king_gift_group')}
                                variant='outlined'
                                />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={6}>
                            <TextField
                                label="Kings Council"
                                value={user.kings_council}
                                onChange={handleChange('kings_council')}
                                fullWidth
                                error={isError('kings_council')}
                                helperText={getErrorMessage('kings_council')}
                                variant='outlined'
                                />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={6}>
                            <TextField
                                label="TCCF Past President"
                                value={user.tccf_past_president}
                                onChange={handleChange('tccf_past_president')}
                                fullWidth
                                error={isError('tccf_past_president')}
                                helperText={getErrorMessage('tccf_past_president')}
                                variant='outlined'
                                />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={6}>
                            <TextField
                                label="Year Joined"
                                value={user.year_joined}
                                onChange={handleChange('year_joined')}
                                fullWidth
                                error={isError('year_joined')}
                                helperText={getErrorMessage('year_joined')}
                                variant='outlined'
                                />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={6}>
                            <TextField
                                label="Military Branch"
                                value={user.military_branch}
                                onChange={handleChange('military_branch')}
                                fullWidth
                                error={isError('military_branch')}
                                helperText={getErrorMessage('military_branch')}
                                variant='outlined'
                                />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={6}>
                            <TextField
                                label="Military Rank"
                                value={user.military_rank}
                                onChange={handleChange('military_rank')}
                                fullWidth
                                error={isError('military_rank')}
                                helperText={getErrorMessage('military_rank')}
                                variant='outlined'
                                />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={6}>
                            <TextField
                                label="Military Years of Service"
                                value={user.military_years_of_service}
                                onChange={handleChange('military_years_of_service')}
                                fullWidth
                                error={isError('military_years_of_service')}
                                helperText={getErrorMessage('military_years_of_service')}
                                variant='outlined'
                                />
                        </Grid>
                    </Grid>
                    <Typography align="right" component="div" className={classes.marginTop}>
                        <SaveButton
                            disabled={cavalierLoading || deleted}
                            variant="contained"
                            color="primary"
                            onClick={postCavalier}
                            >
                            Update
                        </SaveButton>
                    </Typography>
                </Paper>
            }
            {(customFields.length > 0) &&
                <Paper className={classes.paper}>
                    <Loading isLoading={otherLoading} />
                    <Grid container
                        alignItems="center"
                        justify="space-between"
                        spacing={3}
                        >
                        <Grid item>
                            <Typography variant="h6" component="h3" gutterBottom>
                                Other Information
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        {customFields.map(field => (
                            <Grid item xs={12} sm={6} md={12} lg={6}>
                                <Field
                                    key={field.id}
                                    field={field}
                                    fields={customFields}
                                    value={customValues[field.id]}
                                    values={customValues}
                                    handleChange={handleCustomValue}
                                    admin={admin}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <Typography align="right" component="div" className={classes.marginTop}>
                        <SaveButton
                            disabled={otherLoading || deleted}
                            variant="contained"
                            color="primary"
                            onClick={postOther}
                            >
                            Update
                        </SaveButton>
                    </Typography>
                </Paper>
            }
            {/* !profile &&
                <div className="p-b-xxl">
                    <Grid container
                        alignItems="center"
                        justify="space-between"
                        spacing={3}
                        classes={{ container: classes.grid }}>
                        <Grid item>
                            <Typography variant="h6" component="h3" gutterBottom>
                                Other
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={user.deceased}
                                        onChange={handleChange('deceased')}
                                        value="deceased"
                                        color="primary"
                                        type="checkbox"
                                    />
                                }
                                label="Is Deceased? (Admin Use Only)"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Internal Notes (Admin Use Only)"
                                multiline
                                rowsMax="6"
                                value={user.membership.meta.notes}
                                onChange={handleNotes}
                                fullWidth={true}
                                />
                        </Grid>
                    </Grid>
                </div>
            */}
            <ConfirmEmailDialog
                isOpen={confirmEmailDialogOpen}
                onClose={closeConfirmEmailDialog}
            />
            <ReconcileAccountsDialog
                isOpen={reconcileAccountsDialogOpen}
                onClose={closeReconcileAccountsDialog}
                user={user}
            />
        </form>
    )
}
